/**
 * 블랙잭 — 유리샘 뱅커의 카드 테이블 (UNI-Q41).
 *
 * 이 화면이 반드시 말해야 하는 것 셋:
 *   ① **얼마를 걸면 얼마를 받나** — 이기면 판돈 × 2, 자연 블랙잭이면 × 2.5 + 잭팟. 부족분은 서버가 보충한다
 *   ② **지금 어디까지 왔나** — 접수/카드/결과 단계와 남은 초, 내 손패와 뱅커 공개 카드
 *   ③ **규칙** — 숫자 카드만, 21 넘으면 버스트, 이번 판의 뱅커 받기 기준 (사용자: 도움말에 적어라)
 *
 * ⛔ 값을 문장에 손으로 적지 않는다 — `world/blackjack.js` 미러와 서버가 보낸 `cfg` 에서 읽는다.
 * 배당 산술은 서버만 한다. 화면은 서버가 보낸 지급액과 보충액을 그린다.
 */
import { S, esc } from '../../core/state.js';
import {
  BJ_BANKER_STAND, BJ_CARD_MAX, BJ_DECK_COPIES, BJ_NATURAL_MULT, BJ_STAKE_UNIT, BJ_TARGET, BJ_WIN_MULT,
} from '../../world/blackjack.js';
import { betQuick, buyBtn, chanceNotice, fold, muted, npcHead, num, sec, maxBeforeTax, taxOf, taxPct, withTax } from './common.js';
import { starMark } from '../star.js';

const OUTCOME_KO = { natural: '자연 블랙잭', win: '승리', push: '무승부', lose: '패배' };

const clampUnits = (n) => {
  const v = Math.floor(Number(n) || 0);
  return Number.isSafeInteger(v) ? Math.max(1, v) : 1;
};
const bjUnit = () => Number(S.blackjack?.cfg?.stakeUnit) || BJ_STAKE_UNIT;

/** 카드 한 줄 — 숫자만. 뱅커의 숨은 카드는 뒷면 */
const cards = (list, hidden = false) => `<div class="bjcards">${(list || []).map((c) => `<i>${num(c)}</i>`).join('')}`
  + `${hidden ? '<i class="back">?</i>' : ''}</div>`;

const left = (t) => Math.max(0, Math.ceil(t.until - Date.now() / 1000));

/** 남은 초만 글자로 갱신한다 — 패널을 다시 그리지 않는다(누르고 떼는 사이 버튼이 바뀌면 클릭이 사라진다) */
let clockId = 0;
function startClock() {
  if (clockId) return;
  clockId = setInterval(() => {
    if (!S.blackjack?.table || S.panel !== 'blackjack') { clearInterval(clockId); clockId = 0; return; }
    const el = document.querySelector('#pbody .bjsec');
    if (!el) return;
    el.textContent = `${left(S.blackjack.table)}초`;
  }, 250);
}

/** 입력칸과 앉기 버튼 문구만 바꾼다 — 패널 전체를 갈아치우면 타이핑 중 포커스가 끊긴다(경주 syncDraft 와 같은 이유) */
function syncDraft(typing = false) {
  const units = clampUnits(S.bjUnits);
  const unit = bjUnit();
  // 타이핑 중에는 칸을 고쳐 쓰지 않는다 — 실제 판돈은 아래 줄과 버튼이 말한다(칸을 떠날 때 단위로 맞춘다)
  const input = document.querySelector('#bjstake');
  if (input && !typing && Number(input.value) !== units * unit) input.value = String(units * unit);
  // 버튼은 실제로 나가는 은화(판돈 + 왕도 세금)를 적는다 — 슬롯과 같다(UNI-Q200 · 사용자 「이것도 표식에 세금 포함으로 표기를 해야지」)
  const btn = document.querySelector('#pbody .bjjoin button');
  if (btn) btn.textContent = bjJoinLabel(units * unit);
  const pay = document.querySelector('#pbody [data-bj-pay]');
  if (pay) pay.textContent = bjPayText(units * unit);
}
const bjJoinLabel = (stake, y) => `베팅 · ${num(withTax(stake, y))} 은화`;
const bjPayText = (stake, y) => `판돈 ${num(stake)} + 왕도 세금 ${num(taxOf(stake, y))} (${taxPct(y)}%) = ${num(withTax(stake, y))} 은화가 나간다.`;
/** 칸에 친 은화 → 판돈 배수(단위로 내림, 최소 1배). 칸·버튼은 은화로 적고 서버에는 배수를 보낸다 */
export function setStake(silver, typing = false) { S.bjUnits = clampUnits(Math.floor((Number(silver) || 0) / bjUnit())); syncDraft(typing); }
/** 간편 버튼 — 은화 금액을 더한다. 아직 판돈을 안 고른 칸(0)은 0 에서 더한다 */
export function addStake(silver) {
  const base = S.bjUnits ? clampUnits(S.bjUnits) : 0;
  S.bjUnits = clampUnits(base + Math.floor((Number(silver) || 0) / bjUnit())); syncDraft();
}

function rules(cfg) {
  const jackpotPct = Math.round((Number(cfg?.jackpotRate) || 0) * 100);
  const bankerStand = Number(cfg?.bankerStand) || BJ_BANKER_STAND;
  // 2026-10-21 「논문 쓰니?」 — 규칙 일곱 줄은 한 줄 요약 + [자세히] 안(문장은 그대로 둔다)
  return fold('카드 구성·뱅커 규칙·공동 재원과 잭팟',
    `· 카드는 무늬 없는 <b>숫자 1~${BJ_CARD_MAX}</b> 뿐이다(숫자마다 ${BJ_DECK_COPIES}장). 적힌 수가 곧 점수이고 에이스 특례는 없다.<br>`
    + `· 합이 <b>${BJ_TARGET}</b> 에 가까울수록 좋고, 넘으면 <b>버스트</b>로 진다. 첫 두 장이 정확히 ${BJ_TARGET} 이면 <b>자연 블랙잭</b>이다.<br>`
    + `· 이번 판 뱅커는 합 <b>${bankerStand} 미만이면 한 장 더 받고, ${bankerStand} 이상이면 멈춘다.</b> 버스트할 카드를 뽑으면 <b>정해진 확률(뱅커 유리도)로 한 번 다시 뽑는다</b>. 뱅커가 버스트하면 버스트하지 않은 플레이어가 모두 이긴다.<br>`
    + `· 판돈은 <b>${num(BJ_STAKE_UNIT)} 은화 단위</b>, 상한은 없다. 원금 포함 승리는 판돈 × ${BJ_WIN_MULT}, 자연 블랙잭은 × ${BJ_NATURAL_MULT}, 무승부는 원금을 돌려받는다.<br>`
    + '· 판돈 전액이 공동 재원에 쌓인다. <b>배당이 부족하면 서버가 보충해 전액 지급한다.</b> 패배한 판돈도 전액 공동 재원에 남는다.<br>'
    + `· 기본 지급 후 남은 공동 재원의 ${jackpotPct}%를 자연 블랙잭 승자끼리 나눠 잭팟으로 받는다.<br>`
    + '· 접속이 끊기거나 시간이 다하면 자동으로 스탠드한다. 배당은 <b>창고</b>로 들어간다.');
}

export function render({ y, town, at }) {
  let h = npcHead() + chanceNotice();
  if (!town) return h + muted('블랙잭은 유리샘 마을의 뱅커 카르도에게서 이용할 수 있다.');
  const d = S.blackjack;
  if (!d) return h + muted('뱅커가 카드를 정리하는 중…');
  const t = d.table;
  const cfg = d.cfg || {};
  const unit = Number(cfg.stakeUnit) || BJ_STAKE_UNIT;
  const units = clampUnits(S.bjUnits);
  const stake = units * unit;
  const available = Math.max(0, Number(y.silver || 0) + Number(S.vault?.silver || 0));
  const me = S.you ? (t?.tickets || []).find((x) => x.id === S.you.id || x.name === S.you.name) : null;
  const pool = `<div class="racepot bjpot"><span>🃏 공동 재원</span><b>${num(d.pool)} 은화</b>
    <small>배당이 모자라면 서버가 채워 전액 지급</small></div>`;
  h += pool + `<div class="ux-facts"><span class="ux-fact">목표 <b><span class="ux-number">${BJ_TARGET}</span><small>넘으면 패배</small></b></span><span class="ux-fact">승리 <b><span class="ux-number">×${BJ_WIN_MULT}</span><small>판돈</small></b></span><span class="ux-fact">자연 블랙잭 <b><span class="ux-number">×${BJ_NATURAL_MULT}</span>${Number(cfg.jackpotRate) > 0 ? '<small>+ 잭팟</small>' : ''}</b></span></div>`
    + muted('배당은 원금 포함 · 무승부는 원금 반환 · 시간 초과·접속 종료 시 자동으로 스탠드');

  if (!t) {
    h += d.on ? sec('다음 판 준비 중', muted('뱅커가 카드를 섞는다. 잠시 뒤 접수가 열린다.'))
      : sec('휴업', muted('뱅커가 자리를 비웠다. 지금은 카드를 돌리지 않는다.'));
    return h + rules(cfg);
  }
  startClock();
  const secs = left(t);
  const seated = t.tickets.length;

  if (t.phase === 'join') {
    h += '<div class="bj-table">'+sec(`제 ${num(t.id)}회차 · 접수 <em class="bjsec">${secs}초</em>`,
      '<div class="bjhand banker"><span class="nm">뱅커 · 카드를 돌리기 전</span><div class="bjcards"><i class="back">?</i><i class="back">?</i></div></div>' +
      muted((seated ? num(seated) + '명이 앉았다' : '참가자 없음 · 혼자면 뱅커와 1:1')
        + ` · 차례마다 ${num(cfg.turnSecs)}초`));
    if (me) {
      h += sec('내 자리', `<div class="kv"><span>판돈</span><b>${num(me.stake)} 은화</b></div>` + muted('카드가 돌면 여기서 히트·스탠드를 선택한다.'));
    } else {
      // 지난 판 표가 남아 있어도 앉기 버튼을 잠그지 않는다 — 그 표를 정리하는 것이 바로 이 버튼의 서버 관문이다(gateBjJoin:
      //   정산됐으면 표를 지우고, 아니면 판돈을 창고로 돌려준 뒤 다시 앉으라고 한다). 잠그면 영영 못 푼다(2026-10-20 운영 「지난 판 정리 중」 고착)
      const more = [];
      const stale = y.inv?.bj && y.inv.bj.round !== t.id
        ? muted(`지난 제 ${num(y.inv.bj.round)} 판 표가 남아 있다 — 앉기를 누르면 먼저 정리(정산 확인·판돈 반환)하고, 그다음 다시 앉으면 된다.`) : '';
      h += `<div class="sec">${stale}<h3>내 자리의 판돈</h3><label for="bjstake" class="qt">${num(unit)} 은화 단위 · 상한 없음</label><div class="row">
        <input id="bjstake" type="number" aria-label="블랙잭 판돈 은화" inputmode="numeric" min="${unit}" step="${unit}" value="${stake}"
          oninput="act.bjStake(this.value)" onchange="act.bjStakeSet(this.value)" placeholder="걸 은화 (${num(unit)} 단위)">
        </div>${betQuick('act.bjStakeAdd', `act.bjStakeSet(${Math.max(1, Math.floor(maxBeforeTax(available, y) / unit)) * unit})`)}${muted(`<b data-bj-pay>${bjPayText(stake, y)}</b><br>`
          + `가방 <b>${num(y.silver)}</b> + 창고 <b>${num(S.vault?.silver || 0)}</b> = <b>${num(available)}</b> 은화`)}
        <div class="row bjjoin">${buyBtn(bjJoinLabel(stake, y), 'act.bjJoin()', { y, at, need: 'bjJoin', price: withTax(stake, y), more })}</div></div>`;
    }
    h+='</div>';
  } else {
    const label = t.phase === 'play' ? `카드 <em class="bjsec">${secs}초</em>` : '결과';
    h += '<div class="bj-table">'+sec(`제 ${num(t.id)}회차 · ${label}`,
      `<div class="bjhand banker"><span class="nm">뱅커${t.bankerHidden ? ' · 공개 카드' : ''}</span>${cards(t.banker, t.bankerHidden)}`
      + `<span class="qt">${t.bankerHidden ? `공개 ${num(t.bankerTotal)}` : `합 ${num(t.bankerTotal)}${t.bankerTotal > BJ_TARGET ? ' · 버스트' : ''}`}</span></div>`);
    if (me) {
      const mineTotal = num(me.total);
      const status = me.bust ? '버스트' : me.natural ? '자연 블랙잭' : me.done ? '스탠드' : '고르는 중';
      let act = '';
      if (t.phase === 'play' && !me.done) {
        act = `<div class="row bjact"><button onclick="act.bjHit()">히트 · 한 장 더</button><button class="ghost" onclick="act.bjStand()">스탠드 · 멈추기</button></div>`;
      } else if (t.phase === 'show' && me.outcome) {
        const paidTxt = me.paid === null || me.paid === undefined ? '정산 중…' : num(me.paid) + ' 은화 · 창고 지급' + (me.jackpot > 0 ? ' (잭팟 ' + num(me.jackpot) + ')' : '');
        act = `<div class="kv"><span>${esc(OUTCOME_KO[me.outcome] || me.outcome)}</span><b>${paidTxt}</b></div>`;
      }
      h += sec(`내 카드 · 합 ${mineTotal} · ${status}`, cards(me.cards) + `<div class="kv"><span>판돈</span><b>${num(me.stake)} 은화</b></div>` + act);
    }
    if (!me) h += muted(t.phase === 'play' ? '이번 회차는 관전 중이다. 다음 접수 때 참가할 수 있다.' : '결과가 끝나면 다음 판 접수가 열린다.');
    h+='</div>';
    const others = t.tickets.filter((x) => x !== me);
    if (others.length) {
      h += fold(`같은 판의 카드 · ${num(others.length)}명`, others.map((x) => {
        const tail = t.phase === 'show' && x.outcome
          ? esc(OUTCOME_KO[x.outcome] || x.outcome) + ' · ' + (x.paid === null || x.paid === undefined ? '정산 중' : num(x.paid) + ' 은화')
          : x.bust ? '버스트' : x.done ? '스탠드' : '고르는 중';
        return `<div class="bjhand"><span class="nm">${starMark(x.name)}${esc(x.name)} <span class="qt">${num(x.stake)} 은화</span></span>${cards(x.cards)}<span class="qt">합 ${num(x.total)} · ${tail}</span></div>`;
      }).join(''));
    }
    if (t.phase === 'show' && t.result) {
      const r = t.result;
      h += fold('이번 판 전체 정산 내역', `<div class="kv"><span>판돈 총합</span><b>${num(r.stakes)} 은화</b></div>`
        + `<div class="kv"><span>공동 재원 적립</span><b>${num(r.accrual)} 은화</b></div>`
        + `<div class="kv"><span>서버 보충</span><b>${num(r.mintedShortfall || 0)} 은화</b></div>`
        + `<div class="kv"><span>지급 (잭팟 포함)</span><b>${num(r.totalPaid)} (${num(r.jackpotPaid)})</b></div>`
        + `<div class="kv"><span>남은 공동 재원</span><b>${num(r.poolAfter)} 은화</b></div>`);
    }
  }
  return h + rules(cfg);
}
